import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Text, Html } from '@react-three/drei';
import { ROOMS } from '../../rooms';
import { useWorldStore } from '../../state/worldStore';
import { useHypeU, classCtl } from '../../city/hypeu';
import { Performer } from '../VenueStage';
import { useNarrow } from '../SceneChat';

// A HYPE-U CLASSROOM'S PEOPLE AND BOARD (city/hypeu: one room id per course).
// Khari: "a small classroom with the artist/instructor standing in front of a
// whiteboard … the whiteboard will contain images and content (when
// available)."
//
//   THE INSTRUCTOR is the course's host (the learn page's), standing at
//     rooms.js `teacher` — just past the board's right end, out of its way —
//     facing the class; their speaking face moves while their voice plays
//     (CourseRunner → useHypeU `talking`).
//   THE BOARD (rooms.js `whiteboard`, its face in room space) IS THE PAGE
//     (2026-11-01, Khari: "the content is sitting over the instructor. We have
//     enough room to make the whiteboard taller so the content can fit there
//     instead of the floating modal"): the class's name in blue marker, the
//     page's title in red under it, and the page itself in a hand anyone can
//     read, in two columns sized down until they fit (FitText). A question to
//     the instructor is written up the same way while it is open — yours in
//     red, their answer beneath. On a phone the board would be a sliver, so it
//     keeps only the titles and the page reads in CourseRunner's sheet.
//   THE CONTROLS sit along the board's bottom edge (drei Html, so they stay
//     under the board as you look around): back, play, next, the page, and
//     "Ask" (Khari: "Place an 'ask question' button next to the back/next
//     pagination at the bottom of the whiteboard"). They press city/hypeu
//     `classCtl` — CourseRunner owns the audio and the asking.
const BASE = import.meta.env.BASE_URL || '/';
const MARKER = `${BASE}fonts/PermanentMarker-Regular.ttf`;
const HAND = `${BASE}fonts/PatrickHand-Regular.ttf`;
const BLUE = '#1f3c88';
const RED = '#b3261e';
const INK = '#1d2433';
const SLATE = '#4a5878';
const GOLD = '#d8a640';
const NAVY = 'rgba(18,26,51,0.94)';

/** Paragraphs into two columns of about the same length (a long paragraph splits at a sentence). */
function twoColumns(paras) {
  const list = paras.filter(Boolean);
  const total = list.reduce((n, p) => n + p.length, 0);
  if (list.length === 0) return [];
  if (total < 260) return [list.join('\n\n')];
  const left = []; let n = 0; let i = 0;
  while (i < list.length && n + list[i].length <= total / 2) { left.push(list[i]); n += list[i].length; i += 1; }
  let right = list.slice(i);
  if (i < list.length) {
    const p = list[i]; const need = total / 2 - n;
    const sentences = p.match(/[^.!?]+[.!?]+["'’”)\]]*\s*|[^.!?]+$/g) || [p];
    let a = '';
    for (const s of sentences) { if (a.length + s.length / 2 > need) break; a += s; }
    if (a.trim()) { left.push(a.trim()); right = [p.slice(a.length).trim(), ...list.slice(i + 1)].filter(Boolean); }
  }
  if (!left.length) return [right.join('\n\n')];
  if (!right.length) return [left.join('\n\n')];
  return [left.join('\n\n'), right.join('\n\n')];
}

/**
 * The page's words, sized to the space they have: an optional lead (the
 * lesson's opening words, or your question) across the top, then one or two
 * columns. Every block reports its height once troika has laid it out; if the
 * lot runs past the bottom, everything is set a size smaller and laid out again.
 */
function FitText({ lead, leadColor = SLATE, leadFont = HAND, cols, x0, width, top, height, base }) {
  const [k, setK] = useState(1);
  const [leadH, setLeadH] = useState(0);
  const sig = `${lead || ''}§${cols.join('§')}§${base}§${width}§${height}`;
  const kRef = useRef(1);
  kRef.current = k;
  const seen = useRef({});
  useEffect(() => { seen.current = {}; setK(1); setLeadH(0); }, [sig]);
  const gap = base * 0.9;
  const blocks = cols.length + (lead ? 1 : 0);
  const report = (key) => (mesh) => {
    const b = mesh?.textRenderInfo?.blockBounds;
    if (!b) return;
    const h = b[3] - b[1];
    seen.current[key] = { k: kRef.current, h };
    if (key === 'lead' && Math.abs(h - leadH) > 1e-5) setLeadH(h);
    const now = Object.values(seen.current).filter((v) => v.k === kRef.current);
    if (now.length < blocks) return;
    const total = (lead ? (seen.current.lead?.h || 0) + gap : 0) + Math.max(0, ...cols.map((_, i) => seen.current[`c${i}`]?.h || 0));
    if (total > height && kRef.current > 0.5) {
      seen.current = {};
      setK(Math.max(0.5, kRef.current * Math.max(0.8, Math.sqrt(height / total) * 0.97)));
    }
  };
  const fs = base * k;
  const colGap = base * 1.6;
  const colW = cols.length > 1 ? (width - colGap) / 2 : width;
  const yCols = top - (lead ? leadH + gap : 0);
  return (
    <>
      {lead && (
        <Text font={leadFont} fontSize={fs} maxWidth={width} anchorX="left" anchorY="top" lineHeight={1.25} position={[x0, top, 0]} color={leadColor} onSync={report('lead')}>
          {lead}
        </Text>
      )}
      {cols.map((t, i) => (
        <Text key={i} font={HAND} fontSize={fs} maxWidth={colW} anchorX="left" anchorY="top" lineHeight={1.28} position={[x0 + i * (colW + colGap), yCols, 0]} color={INK} onSync={report(`c${i}`)}>
          {t}
        </Text>
      ))}
    </>
  );
}

/** What the board says: the class and, once it is running, the page (or the question). */
function boardContent({ title, lesson, qa, host, seated }) {
  const first = (host || 'Your teacher').split(' ')[0];
  if (qa?.open && (qa.q || qa.note)) {
    return {
      heading: 'Your question',
      lead: qa.q ? `“${qa.q}”` : null, leadColor: RED,
      paras: qa.thinking ? [`${first} is thinking…`] : qa.a ? String(qa.a).split(/\n{2,}/) : qa.note ? [qa.note] : [],
    };
  }
  if (!lesson || (!seated && lesson.state !== 'done')) return { big: true, heading: `Take a seat — ${first} will begin` };
  if (lesson.state === 'unwritten') return { big: true, heading: `${first} hasn't written this class yet` };
  if (lesson.state === 'done') {
    return { heading: 'Class dismissed', paras: [`Thanks for sitting in. Questions? Ask ${first} — the button is under the board.`, '↺ starts the lesson again.'] };
  }
  return {
    heading: lesson.pageTitle || (lesson.total ? `Part ${lesson.page}` : title),
    lead: lesson.intro || null,
    paras: lesson.body || [],
  };
}

function Whiteboard({ spec, k, title, host, narrow }) {
  const lesson = useHypeU((s) => s.lesson);
  const qa = useHypeU((s) => s.qa);
  const seated = useWorldStore((s) => s.seated?.phase === 'seated');
  if (!spec) return null;
  const [w, h] = spec.size.map((v) => v * k);
  const pos = spec.pos.map((v) => v * k);
  const c = boardContent({ title, lesson, qa, host, seated });
  const pad = h * 0.06;
  const big = c.big || narrow;
  return (
    <group position={pos} rotation={[0, spec.rotY || 0, 0]}>
      <Suspense fallback={null}>
        <group position={[0, 0, 0.004 * k]}>
          {big ? (
            <>
              <Text font={MARKER} fontSize={h * 0.15} maxWidth={w * 0.88} textAlign="center" anchorX="center" anchorY="middle" lineHeight={1.05} position={[0, h * 0.12, 0]} rotation={[0, 0, -0.012]} color={BLUE}>
                {title}
              </Text>
              <mesh position={[0, -h * 0.04, 0]} rotation={[0, 0, 0.008]}>
                <planeGeometry args={[w * 0.5, h * 0.008]} />
                <meshBasicMaterial color={BLUE} />
              </mesh>
              <Text font={MARKER} fontSize={h * 0.07} maxWidth={w * 0.84} textAlign="center" anchorX="center" anchorY="middle" position={[0, -h * 0.16, 0]} rotation={[0, 0, 0.01]} color={RED}>
                {c.heading}
              </Text>
            </>
          ) : (
            <>
              <Text font={MARKER} fontSize={h * 0.068} maxWidth={w * 0.7} anchorX="left" anchorY="top" position={[-w / 2 + pad, h / 2 - pad, 0]} rotation={[0, 0, -0.006]} color={BLUE}>
                {title}
              </Text>
              <Text font={MARKER} fontSize={h * 0.056} maxWidth={w - pad * 2} anchorX="left" anchorY="top" position={[-w / 2 + pad, h / 2 - pad - h * 0.092, 0]} rotation={[0, 0, 0.004]} color={RED}>
                {c.heading}
              </Text>
              <mesh position={[0, h / 2 - pad - h * 0.172, 0]} rotation={[0, 0, 0.003]}>
                <planeGeometry args={[w - pad * 2, h * 0.006]} />
                <meshBasicMaterial color={BLUE} />
              </mesh>
              <FitText
                lead={c.lead} leadColor={c.leadColor || SLATE}
                cols={twoColumns(c.paras || [])}
                x0={-w / 2 + pad} width={w - pad * 2}
                top={h / 2 - pad - h * 0.205} height={h - pad * 1.7 - h * 0.205}
                base={h * 0.048}
              />
            </>
          )}
        </group>
      </Suspense>
      {!narrow && <BoardControls h={h} k={k} host={host} />}
    </group>
  );
}

// ── THE CONTROLS UNDER THE BOARD ─────────────────────────────────────────────
const round = (on = true, size = 34) => ({
  cursor: on ? 'pointer' : 'default', opacity: on ? 1 : 0.35, width: size, height: size, borderRadius: '50%', flex: 'none',
  border: `1px solid ${GOLD}88`, background: 'rgba(255,255,255,0.06)', color: GOLD, font: '700 13px system-ui', padding: 0,
});

/** Back · play · next · the page · Ask — and, asking, the suggested questions and a box to type in. */
export function ClassControls({ host, style }) {
  const lesson = useHypeU((s) => s.lesson);
  const qa = useHypeU((s) => s.qa);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (qa?.open) setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 50); }, [qa?.open]);
  if (!lesson) return null;
  const first = (host || 'Your teacher').split(' ')[0];
  const ready = lesson.state !== 'unwritten' && lesson.state !== 'finding';
  const playing = lesson.state === 'playing';
  const send = () => { const t = draft.trim(); if (!t || qa?.thinking) return; setDraft(''); classCtl.send?.(t); };
  const status = lesson.note || (lesson.total
    ? `Page ${Math.min(lesson.page, lesson.total)} of ${lesson.total}${lesson.state === 'loading' ? ' · getting ready' : lesson.state === 'between' ? ' · next up' : lesson.state === 'done' ? ' · done' : ''}`
    : '');
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch', fontFamily: 'system-ui, sans-serif', color: '#f7f3ea', ...style }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: NAVY, border: `1px solid ${GOLD}55`, borderRadius: 999, padding: '6px 8px 6px 10px', boxShadow: '0 10px 30px rgba(0,0,0,0.45)', alignSelf: 'center' }}>
        <button type="button" aria-label="Back a page" style={round(ready && lesson.page > 1)} disabled={!ready || lesson.page <= 1} onClick={() => classCtl.back?.()}>⏮</button>
        <button type="button" aria-label={playing ? 'Pause' : lesson.state === 'done' ? 'Start again' : 'Play'} style={{ ...round(ready, 40), background: GOLD, color: '#121a33' }} disabled={!ready} onClick={() => classCtl.toggle?.()}>
          {playing ? '❚❚' : lesson.state === 'loading' ? '…' : lesson.state === 'done' ? '↺' : '▶'}
        </button>
        <button type="button" aria-label="Next page" style={round(ready && lesson.page < lesson.total)} disabled={!ready || lesson.page >= lesson.total} onClick={() => classCtl.next?.()}>⏭</button>
        <span style={{ flex: '0 1 auto', minWidth: 0, fontSize: 12, opacity: 0.75, padding: '0 6px', whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{status}</span>
        <button
          type="button"
          onClick={() => (qa?.open ? classCtl.closeAsk?.() : classCtl.openAsk?.())}
          style={{ cursor: 'pointer', borderRadius: 999, padding: '8px 14px', border: `1px solid ${GOLD}`, background: qa?.open ? GOLD : 'transparent', color: qa?.open ? '#121a33' : GOLD, font: '700 12.5px system-ui', whiteSpace: 'nowrap' }}
        >
          {qa?.open ? 'Back to the lesson' : `💬 Ask ${first}`}
        </button>
      </div>
      {qa?.open && (
        <div style={{ background: NAVY, border: `1px solid ${GOLD}55`, borderRadius: 14, padding: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {qa.chips?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {qa.chips.map((q, i) => (
                <button key={i} type="button" disabled={qa.thinking} onClick={() => classCtl.chip?.(i)} style={{ cursor: qa.thinking ? 'default' : 'pointer', textAlign: 'left', borderRadius: 10, padding: '6px 10px', border: '1px solid rgba(216,166,64,0.35)', background: 'rgba(255,255,255,0.05)', color: '#f7f3ea', font: '500 12px system-ui' }}>
                  {q}
                </button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => { setDraft(e.target.value); classCtl.typing?.(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } if (e.key === 'Escape') { e.stopPropagation(); classCtl.closeAsk?.(); } }}
              placeholder={`Ask ${first} about this page…`}
              maxLength={500}
              style={{ flex: 1, minWidth: 0, borderRadius: 10, border: '1px solid rgba(216,166,64,0.35)', background: 'rgba(255,255,255,0.06)', color: '#f7f3ea', padding: '9px 11px', font: '14px system-ui', outline: 'none' }}
            />
            {/* Block the send, never the box (the chat-input rule). */}
            <button type="button" onClick={send} disabled={!draft.trim() || qa.thinking} style={{ cursor: draft.trim() && !qa.thinking ? 'pointer' : 'default', opacity: draft.trim() && !qa.thinking ? 1 : 0.45, borderRadius: 10, border: 'none', background: GOLD, color: '#121a33', padding: '0 14px', font: '700 13px system-ui' }}>
              {qa.thinking ? '…' : 'Ask'}
            </button>
          </div>
          {(qa.voiceNote || qa.cta) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, opacity: 0.85 }}>
              {qa.voiceNote && <span style={{ flex: 1 }}>{qa.voiceNote}</span>}
              {qa.cta && (
                <a href={qa.cta.href} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto', color: '#121a33', background: GOLD, borderRadius: 999, padding: '5px 12px', font: '700 12px system-ui', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  {qa.cta.label}
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BoardControls({ h, k, host }) {
  const seated = useWorldStore((s) => s.seated?.phase === 'seated');
  const lesson = useHypeU((s) => s.lesson);
  if (!seated || !lesson) return null;
  // Just under the frame, so the buttons hang along the board's bottom edge.
  return (
    <Html position={[0, -h / 2 - 0.03 * k, 0.01 * k]} zIndexRange={[45, 40]} style={{ pointerEvents: 'none' }}>
      <div style={{ transform: 'translateX(-50%)', width: 'min(620px, 86vw)', pointerEvents: 'auto' }}>
        <ClassControls host={host} />
      </div>
    </Html>
  );
}

export default function Classroom({ roomId, k = 1 }) {
  const room = ROOMS[roomId];
  const course = room?.course;
  const talking = useHypeU((s) => s.talking);
  const narrow = useNarrow();
  const goal = useRef(null);   // they stay at the board
  if (!course) return null;
  return (
    <>
      {course.hostId && room.teacher && (
        // Keyed by the classroom: every course's room is the same model, so the
        // room stays mounted floor to floor — the instructor must not.
        <Performer key={`${roomId}:${course.hostId}`} a={course.hostId} name={course.hostName} mark={room.teacher} k={k} light={(room.light ?? 1) * 0.45} talking={talking} goal={goal} />
      )}
      <Whiteboard spec={room.whiteboard} k={k} title={course.title} host={course.hostName} narrow={narrow} />
    </>
  );
}
